import React, {useEffect} from "react";
import {Link} from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

const Step = ({number, children}) => (
    <Box sx={{display: "flex", gap: 1.75, alignItems: "flex-start"}}>
        <Box
            sx={{
                flex: "0 0 auto",
                width: 30,
                height: 30,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                backgroundColor: "#EEF4FF",
                color: "#2B6BFD",
                fontSize: 14,
                fontWeight: 700,
            }}
        >
            {number}
        </Box>
        <Typography sx={{pt: 0.35, color: "#3E4551", lineHeight: 1.65}}>{children}</Typography>
    </Box>
);

const GuideSection = ({title, description, children}) => (
    <Paper
        component="section"
        variant="outlined"
        sx={{
            p: {xs: 2.5, sm: 4},
            borderColor: "#E2E5EA",
            borderRadius: "16px",
            boxShadow: "0 12px 34px rgba(18, 28, 45, 0.05)",
        }}
    >
        <Typography component="h2" sx={{fontSize: {xs: 23, sm: 26}, fontWeight: 700}}>
            {title}
        </Typography>
        {description && (
            <Typography sx={{mt: 1, color: "#626A78", lineHeight: 1.65}}>{description}</Typography>
        )}
        <Box sx={{mt: 2.75, display: "flex", flexDirection: "column", gap: 2}}>{children}</Box>
    </Paper>
);

const ZoomGuide = () => {
    useEffect(() => {
        const previousTitle = document.title;
        const description = document.querySelector('meta[name="description"]');
        const previousDescription = description?.getAttribute("content");

        document.title = "Zoom Integration Guide | Libretto";
        description?.setAttribute(
            "content",
            "How to connect, use, and remove the Libretto app for Zoom cloud recording imports."
        );

        return () => {
            document.title = previousTitle;
            if (description && previousDescription !== null && previousDescription !== undefined) {
                description.setAttribute("content", previousDescription);
            }
        };
    }, []);

    return (
        <Box sx={{minHeight: "100vh", backgroundColor: "#F7F8FB", color: "#16181D"}}>
            <Box component="header" sx={{backgroundColor: "#FFFFFF", borderBottom: "1px solid #E7E9EE"}}>
                <Container
                    maxWidth="md"
                    sx={{height: 72, display: "flex", alignItems: "center", justifyContent: "space-between"}}
                >
                    <Box component={Link} to="/" sx={{display: "flex", alignItems: "center"}} aria-label="Libretto home">
                        <Box component="img" src="/v2-logo.svg" alt="Libretto" sx={{width: 106, height: "auto"}} />
                    </Box>
                    <Button component={Link} to="/support" variant="outlined" sx={{borderRadius: "9px", px: 2}}>
                        Support
                    </Button>
                </Container>
            </Box>

            <Container component="main" maxWidth="md" sx={{py: {xs: 5, sm: 7}}}>
                <Button
                    component={Link}
                    to="/support"
                    startIcon={<ArrowBackOutlinedIcon />}
                    sx={{mb: 3, color: "#626A78", px: 0}}
                >
                    Back to support
                </Button>

                <Box sx={{maxWidth: 720, mb: {xs: 4, sm: 5}}}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            mb: 2,
                            display: "grid",
                            placeItems: "center",
                            color: "#2B6BFD",
                            backgroundColor: "#EEF4FF",
                            borderRadius: "14px",
                        }}
                    >
                        <CloudDownloadOutlinedIcon />
                    </Box>
                    <Typography
                        sx={{
                            mb: 1.25,
                            color: "#2B6BFD",
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                        }}
                    >
                        Libretto + Zoom
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{fontSize: {xs: 36, sm: 50}, lineHeight: 1.1, fontWeight: 750, letterSpacing: "-0.035em"}}
                    >
                        Zoom integration guide
                    </Typography>
                    <Typography sx={{mt: 2, color: "#626A78", fontSize: {xs: 17, sm: 19}, lineHeight: 1.6}}>
                        Follow these steps to add Libretto to your Zoom account, import cloud recordings, and remove the connection.
                    </Typography>
                </Box>

                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <GuideSection
                        title="Before you begin"
                        description="You need a Libretto account and a Zoom account with at least one completed cloud recording. Local recordings stored only on your computer will not appear in the importer."
                    >
                        <Typography sx={{color: "#3E4551", lineHeight: 1.65}}>
                            Libretto currently imports completed <strong>MP4 video</strong> and <strong>M4A audio</strong> files from Zoom.
                        </Typography>
                    </GuideSection>

                    <GuideSection
                        title="1. Add and connect Libretto"
                        description="Connecting authorizes Libretto to show the Zoom cloud recordings available to your Zoom user."
                    >
                        <Step number="1">Sign in to Libretto and open or create a project.</Step>
                        <Step number="2">Select <strong>Import from Zoom</strong> in the empty project view or the project Files panel.</Step>
                        <Step number="3">Select <strong>Connect Zoom</strong>. Sign in to Zoom if Zoom asks you to.</Step>
                        <Step number="4">Review the permissions on Zoom’s authorization screen, then select <strong>Allow</strong>.</Step>
                        <Step number="5">Zoom returns you to Libretto, where your available cloud recordings are shown.</Step>
                    </GuideSection>

                    <GuideSection
                        title="2. Import and use a Zoom recording"
                        description="The Zoom picker lists completed, supported recording files from your connected account."
                    >
                        <Step number="1">Open <strong>Import from Zoom</strong> in the project where you want the files.</Step>
                        <Step number="2">Find the meeting by its topic and date, then select the MP4 or M4A files you want to use.</Step>
                        <Step number="3">Select <strong>Import</strong>. Libretto securely copies the selected files into that project.</Step>
                        <Step number="4">The imported files appear in the project’s Files panel as they are prepared. You can then edit them like other Libretto media.</Step>
                    </GuideSection>

                    <GuideSection
                        title="3. Disconnect or remove Libretto"
                        description="You can disconnect from inside Libretto or remove the app from your Zoom account."
                    >
                        <Typography sx={{fontWeight: 700}}>Disconnect in Libretto</Typography>
                        <Step number="1">Open any Libretto project and select <strong>Import from Zoom</strong>.</Step>
                        <Step number="2">Beside <strong>Zoom connected</strong>, select <strong>Disconnect</strong>.</Step>

                        <Typography sx={{fontWeight: 700, mt: 1}}>Remove from your Zoom account</Typography>
                        <Step number="1">
                            Sign in to the <Box component="a" href="https://marketplace.zoom.us/user/installed" target="_blank" rel="noreferrer" sx={{color: "#2B6BFD"}}>Zoom App Marketplace</Box>.
                        </Step>
                        <Step number="2">Open <strong>My Library</strong>. In some Marketplace views this is shown as <strong>Manage → Added Apps</strong>.</Step>
                        <Step number="3">Find Libretto, then select <strong>Actions → More (…) → Remove</strong>.</Step>
                        <Step number="4">Choose a reason if Zoom asks for one, then select <strong>Remove</strong> again to confirm.</Step>

                        <Box sx={{mt: 1, p: 2.25, backgroundColor: "#F5F8FF", borderRadius: "12px"}}>
                            <Typography sx={{fontWeight: 700, mb: 0.75}}>What removal does</Typography>
                            <Typography sx={{color: "#526075", lineHeight: 1.65}}>
                                Removing or disconnecting Libretto revokes Zoom access and deletes the stored Zoom connection credentials from Libretto. Libretto can no longer list or import your recordings unless you connect again.
                            </Typography>
                            <Typography sx={{mt: 1, color: "#526075", lineHeight: 1.65}}>
                                Recordings previously imported into a Libretto project remain there because they are separate copies. Removal does not delete your original Zoom recordings or your Libretto account. You can delete imported files in Libretto, or email contact@libretto.fm for account and data deletion help.
                            </Typography>
                        </Box>
                    </GuideSection>

                    <GuideSection title="Troubleshooting">
                        <Typography sx={{fontWeight: 700}}>A recording is missing</Typography>
                        <Typography sx={{color: "#626A78", lineHeight: 1.65}}>
                            Confirm that Zoom has finished processing it, that it is a cloud recording, and that you connected the Zoom account that owns it.
                        </Typography>

                        <Typography sx={{fontWeight: 700, mt: 0.5}}>The connection no longer works</Typography>
                        <Typography sx={{color: "#626A78", lineHeight: 1.65}}>
                            Disconnect Zoom in Libretto, then connect it again and approve the requested permissions.
                        </Typography>

                        <Typography sx={{fontWeight: 700, mt: 0.5}}>Still need help?</Typography>
                        <Typography sx={{color: "#626A78", lineHeight: 1.65}}>
                            Email <Box component="a" href="mailto:contact@libretto.fm?subject=Libretto%20Zoom%20Support" sx={{color: "#2B6BFD"}}>contact@libretto.fm</Box> with your Libretto account email, the meeting topic and approximate date, and any error shown. Never send your password or access tokens.
                        </Typography>
                    </GuideSection>
                </Box>
            </Container>

            <Box component="footer" sx={{mt: 3, borderTop: "1px solid #E7E9EE", backgroundColor: "#FFFFFF"}}>
                <Container
                    maxWidth="md"
                    sx={{py: 3, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2}}
                >
                    <Typography sx={{color: "#7A818E", fontSize: 14}}>© Libretto</Typography>
                    <Box sx={{display: "flex", gap: 2.5}}>
                        <Typography component={Link} to="/support" sx={{color: "#626A78", fontSize: 14, textDecoration: "none"}}>Support</Typography>
                        <Typography component={Link} to="/privacy" sx={{color: "#626A78", fontSize: 14, textDecoration: "none"}}>Privacy</Typography>
                        <Typography component={Link} to="/terms" sx={{color: "#626A78", fontSize: 14, textDecoration: "none"}}>Terms</Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default ZoomGuide;
